import { AfterViewInit, Component, OnInit, QueryList, ViewChildren, inject } from '@angular/core';
import {Observable} from 'rxjs';
import {finalize, map, tap} from 'rxjs/operators';

import {FeatureFlags, FeatureFlagService, SessionService} from '@core';
import {ModalService, ModalType} from '@shared';

import {JobIdInfo, NavigationService, OptimizationService, ProblemInstanceService} from 'planner-shared';

import {OptimizationSolutionComponent} from './solutions/optimization-solution.component';
import {OptimizationSettingsModalComponent} from './settings-modal/optimization-settings-modal.component';
import {RoutesComparisonModalComponent} from './routes-comparison-modal/routes-comparison-modal.component';

@Component({
    selector: 'app-optimization',
    templateUrl: './optimization.component.html',
    styleUrls: ['./optimization.component.scss'],
    standalone: false
})
export class OptimizationComponent implements OnInit, AfterViewInit {
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly optimizationService = inject(OptimizationService);
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly sessionService = inject(SessionService);
    private readonly modalService = inject(ModalService);
    private readonly navigationService = inject(NavigationService);

    public loading: boolean;
    public solutionsLoading: boolean;
    public solutionsLoadingText: string;
    public bestJobIdInfo$: Observable<JobIdInfo>;
    public alternativeJobIdInfos$: Observable<JobIdInfo[]>;
    public objectiveWeights$: Observable<{ [key: string]: string }>;
    public isRouteComparisonEnabled: Observable<boolean>;
    public optimizationFinished = false;
    public isRideSharingEnabled: boolean;
    public isRideSharingInRoute: boolean;
    public optimizationSettingsPlacementEnabled: Observable<boolean>;
    @ViewChildren(OptimizationSolutionComponent)
    private optimizationSolutionComponents: QueryList<OptimizationSolutionComponent>;
    private solutionCounter = 0;
    private totalSolutionsCount: number;

    ngOnInit(): void {
        this.initOptimization();
        this.initObjectiveWeights();
        this.isRouteComparisonEnabled = this.featureFlagService.isEnabled(FeatureFlags.RouteComparison);
        this.optimizationSettingsPlacementEnabled
            = this.featureFlagService.isEnabled(FeatureFlags.OptimizationSettingsPlacement);
        this.problemInstanceService.requestProblemInstance()
            .subscribe(problemInstanceResult => {
                this.isRideSharingEnabled = problemInstanceResult.problemInstance.officeInfo.rideSharingEnabledOnClientSide
                    && problemInstanceResult.problemInstance.officeInfo.useRideSharingForOptimization;
            });
    }

    ngAfterViewInit() {
        this.problemInstanceService.getProblemInstance().subscribe(problemInstanceResult => {
            if (this.navigationService.isPrevStepPriorOptimization()
                && problemInstanceResult.problemInstance.jobIdInfos) {
                if (!this.optimizationFinished) {
                    this.stopAndStartOptimization();
                } else {
                    this.startOptimization();
                }
            }
        });
    }

    public openSettingsModal(): void {
        this.modalService.openCustomModal(
            OptimizationSettingsModalComponent,
            () => {
                this.performSettingsUpdate();
            },
            null,
            null,
            true
        );
    }

    public openRouteCompareModal(): void {
        this.modalService.openCustomModal(
            RoutesComparisonModalComponent,
            null,
            null,
            this.isRideSharingInRoute,
            true
        );
    }

    public restartOptimization(): void {
        if (this.sessionService.optimizationRunning) {
            this.modalService.openInformationModal(
                ModalType.Question,
                'RESTART_OPTIMIZATION_INFO',
                () => this.stopAndStartOptimization(),
                null,
                true
            );
        } else {
            this.startOptimization();
        }
        this.isRideSharingInRoute = false;
    }

    public solutionLoading(loadingText: string): void {
        this.solutionsLoadingText = loadingText;
        this.solutionsLoading = true;
    }

    public trackByFn(index: number): number {
        return index;
    }

    public solutionFinished($event: Event) {
        if ($event) {
            this.solutionCounter++;
            if (this.solutionCounter === this.totalSolutionsCount) {
                this.solutionCounter = 0;
                this.optimizationFinished = true;
            }
        }
    }

    public solutionTick(rideSharinginRoute: boolean) {
        this.isRideSharingInRoute = rideSharinginRoute;
    }

    public startOptimization(): void {
        this.solutionsLoading = true;
        this.optimizationFinished = false;
        this.solutionsLoadingText = 'LOADING_INFO_STARTING_OPTIMIZATION_SOLVER';
        this.optimizationService
            .startOptimizationProcess()
            .pipe(
                finalize(() => {
                    setTimeout(() => {
                        this.optimizationSolutionComponents.forEach(i => i.initSolution(true));
                        this.solutionsLoading = false;
                    });
                })
            )
            .subscribe();
    }

    public performSettingsUpdate() {
        this.stopAndStartOptimization();
        this.initObjectiveWeights();
    }

    private initOptimization(): void {
        this.optimizationService.initOptimization();
        this.loading = true;
        this.optimizationFinished = false;
        this.totalSolutionsCount = 0;
        this.bestJobIdInfo$ = this.optimizationService
            .getJobIdInfoWithoutChanges()
            .pipe(
                tap(() => {
                    this.loading = false;
                    this.problemInstanceService.getProblemInstance()
                        .subscribe(problemInstanceResult =>
                            this.totalSolutionsCount = problemInstanceResult.problemInstance.jobIdInfos.length);
                }),
            );
        this.alternativeJobIdInfos$ = this.optimizationService.getJobIdInfosWithChanges();
    }

    private stopAndStartOptimization(): void {
        this.optimizationSolutionComponents.forEach(i => i.stopTimer());
        this.startOptimization();
    }

    private initObjectiveWeights(): void {
        this.objectiveWeights$ = this.problemInstanceService.getProblemInstance().pipe(
            map(result => {
                return {
                    timeWindowWeight: this.getWeightTranslationStr(result.problemInstance.officeInfo.timeWindowWeight),
                    travelTimeWeight: this.getWeightTranslationStr(result.problemInstance.officeInfo.travelTimeWeight),
                    visitHistoryWeight: this.getWeightTranslationStr(
                        result.problemInstance.officeInfo.visitHistoryWeight
                    ),
                    overqualifiedWeight: this.getWeightTranslationStr(
                        result.problemInstance.officeInfo.overqualifiedWeight
                    ),
                    rosterPriority: this.getWeightTranslationStr(
                        result.problemInstance.officeInfo.rosterPriorityWeight
                    )
                };
            })
        );
    }

    private getWeightTranslationStr(weight: number): string {
        switch (weight) {
        case 1:
            return 'LOW';
        case 2:
            return 'MEDIUM';
        case 3:
            return 'HIGH';
        case 6:
            return 'CRITICAL';
        default:
            return null;
        }
    }
}
